/**
 * @author Hashir Kayani
 * @datetime October, 17 2023
 * @description: This component is responsible for showing the active issues
 * related to access-point
 */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subscription, of } from 'rxjs';
import { CONSTANTS } from 'src/app/shared/constants/constants';
import { NotificationService } from 'src/app/shared/errors/notification.service';
import { SharedDataService } from 'src/app/shared/services/shared-data.service';
import { ISharedData } from 'src/app/shared/state-management/state-interfaces';
import { CommonService } from 'src/app/shared/utils/common.service';
import { HelperService } from 'src/app/shared/utils/helper.service';
import { Utility } from 'src/app/shared/utils/utility.service';
import { IUser } from 'src/app/user-management/user-management-interfaces';
import { UserManagementService } from 'src/app/user-management/user-management.service';
import { LANGUAGE_CONSTANTS as LANG_CONST } from 'src/app/shared/constants/language-constants';
import { FloorManagementService } from 'src/app/floor-management/floor-management.service';
import { IDevice } from 'src/app/building-managment/building-unit-details/add-lock/lock-management-interfaces';
import { BuildingManagementService } from 'src/app/building-managment/building-management.service';

@Component({
  selector: 'app-dashboard-active-issues-accesspoint-detail',
  templateUrl: './dashboard-active-issues-accesspoint-detail.component.html',
  styleUrls: ['./dashboard-active-issues-accesspoint-detail.component.scss'],
})
export class DashboardActiveIssuesAccesspointDetailComponent
  implements OnInit, OnDestroy
{
  CONSTANTS = CONSTANTS;
  subscription: Subscription | undefined;
  apiSubscription: Subscription | undefined;
  user: IUser | null | undefined;
  sharedDataSubscription?: Subscription;

  activeTab: string = '';
  readonly LANG_CONST = LANG_CONST;
  siteId!: string;
  id!: string;
  buildingId!: string;
  accessPointId!: string;
  buildingName!: string;
  previousId!: string;
  sharedData?: ISharedData;
  userGroups!: string;
  accessPointCount: number = 0;
  devicesCount: number = 0;
  accessPointCounts!: number;
  lockCount!: number;
  disabledDeviceTab = false;
  disabledUserTab = false;
  noAccessAvailable: boolean = false;
  floorId!: string;
  unitName: any;
  accessPointName: any;
  deviceCount: any;
  accesspointDeviceCount: any;
  floorName: any;
  accessPointFloorName: any;
  wingName: any;
  accesspointWingName: any;
  deviceList!: IDevice[] | undefined;
  usersList!: Observable<IUser[] | undefined>;

  tempHoldBuildingName!: string;

  constructor(
    private route: ActivatedRoute,
    private userManagementService: UserManagementService,
    private helperService: HelperService,
    private notificationService: NotificationService,
    private sharedDataService: SharedDataService,
    private router: Router,
    private commonService: CommonService,
    private floorManagementService: FloorManagementService,
    private buildingManagementService: BuildingManagementService
  ) {}

  ngOnInit(): void {
    this.subscription = this.route.params.subscribe((params) => {
      this.id = Utility.decodeFromBase64(params['id']);
      this.buildingId = Utility.decodeFromBase64(params['buildingId']);
      this.accessPointId = Utility.decodeFromBase64(params['accessPointId']);
      this.floorId = Utility.decodeFromBase64(params['floorId']);
      this.tempHoldBuildingName = Utility.decodeFromBase64(
        params['buildingName']
      );
      this.sharedDataSubscription =
        this.sharedDataService.currentData.subscribe((sharedData) => {
          this.sharedData = sharedData;
          const newSiteId = String(this.sharedData?.selectedSite?.siteId);

          if (this.siteId && this.siteId !== newSiteId) {
            this.router.navigate(['/dashboard/active-issues']);
            return;
          }

          if (this.id && this.previousId !== this.id) {
            this.previousId = this.id;
            this.siteId = newSiteId;
            this.getAccessPointDetail(
              this.siteId,
              this.buildingId,
              this.floorId,
              this.accessPointId
            );
            this.getPartnerDevicesByAccessPointId(this.accessPointId);
            this.getUsersByAccessPointId(this.accessPointId);
          }
        });
    });
  }

  /**
   *
   * @param siteId {string}
   * @param buildingId {string}
   * @param floorId {string}
   * @param accessPointId {string}
   * @returns {accessPointName, deviceCount, floorName, wingName}
   */
  public getAccessPointDetail(
    siteId: string,
    buildingId: string,
    floorId: string,
    accessPointId: string
  ): void {
    this.floorManagementService
      .getAccessPoints(siteId, buildingId, floorId, accessPointId, true)
      .subscribe({
        next: (res: any) => {
          this.accessPointName = res?.accessPointName;
          this.accessPointFloorName = res?.floorName;
          this.accesspointWingName = res?.wingName;
          this.buildingName = res?.buildingName;
        },
        error: (err: Error) => {
          if (window.__dev__) console.log('Get AccessPoint detail', err);
        },
      });
  }

  /**
   * @param accessPointId {string}
   * @returns will return all Partner device data
   */
  private getPartnerDevicesByAccessPointId(accessPointId: string): void {
    this.floorManagementService
      .getPartnerDevicesByAccessPoint(accessPointId)
      .subscribe({
        next: (deviceData: any) => {
          this.deviceList = deviceData as any[];
          if (this.deviceList?.length > 0) {
            this.deviceList = [
              ...this.deviceList.filter((device) => device.isvisble),
            ];

            this.accesspointDeviceCount = this.deviceList.length;
            this.disabledDeviceTab = false;
            this.activeTab = CONSTANTS.DEVICETAB;
          } else {
            this.disabledDeviceTab = true;
          }
        },
        error: (err: Error) => {
          this.disabledTabs();
          if (window.__dev__) console.log('Get Partner Devices: ', err);
        },
      });
  }
  /**
   * @param accessPointId {string}
   * @returns will return all user- by accesspoint id
   */
  private getUsersByAccessPointId(accessPointId: string): void {
    this.buildingManagementService
      .getUserViaAccessPoint(accessPointId)
      .subscribe({
        next: (userData: any) => {
          if (userData?.length > 0) {
            this.usersList = of(userData);
            this.disabledUserTab = false;
          } else {
            this.disabledUserTab = true;
          }
        },
        error: (err: Error) => {
          this.disabledTabs();
          if (window.__dev__) console.log('Get users: ', err);
        },
      });
  }

  disabledTabs(): void {
    this.buildingName = '';
    this.activeTab = CONSTANTS.DEVICETAB;
    this.disabledUserTab = true;
    this.disabledDeviceTab = true;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.apiSubscription?.unsubscribe();
    this.sharedDataSubscription?.unsubscribe();
  }
}
